'use client';

import classNames from 'classnames';
import { useId, useState } from 'react';
import {
  formatCurrencyAmount,
  formatNumberWithSeparator,
} from '../../utils/helpers/formatters';
import { tr } from '../../utils/translations/tr';
import { MaterialIcon } from '../MaterialIcon';
import { CcProductCardTranslations } from './i18n';
import { CcEstimateRate, CcProductCardConfig } from './types';
import { CC_CARD_TEXT, formatCompactAmount } from './utils';

type CcProductCardEstimateProps = {
  rate: CcEstimateRate | undefined;
  config: CcProductCardConfig | undefined;
  /** The expanded block's title, worded for the card's reward kind. */
  title: string;
  /** "Min. monthly spend HK$5,000", already worded; a cashback card repeats it here (FR-1.6.6). */
  minSpend?: string;
  t: CcProductCardTranslations;
  currency: string;
  /** The details were opened or closed: the new state, for analytics. */
  onToggle?: (expanded: boolean) => void;
};

/**
 * The earnings estimate (BRD-01 FR-1.6): the card's local rate turned into money or miles at the
 * market's baseline monthly spend. The reader cannot change the spend here (FR-1.6.9), and the
 * assumption shows wherever a figure does (FR-1.6.7).
 */
export const CcProductCardEstimate: React.FC<CcProductCardEstimateProps> = ({
  rate,
  config,
  title,
  minSpend,
  t,
  currency,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  const baseline = config?.baseline_monthly_spend;
  // FR-1.6.8: without a usable rate or a baseline there is no block — never a zero or a dash.
  if (!rate || !baseline || baseline <= 0) return null;

  const spend = formatCompactAmount(baseline, currency);
  const count = (value: number) =>
    formatNumberWithSeparator({ value, thousandsSeparator: ',' });

  // FR-1.6.4; a monthly cashback cap bounds the monthly figure, and the yearly one with it.
  const monthly =
    rate.kind === 'cashback'
      ? Math.min(
          Math.round((baseline * rate.percent) / 100),
          rate.monthlyCap ?? Infinity,
        )
      : Math.round(baseline / rate.dollarsPerMile);
  if (monthly <= 0) return null;
  const yearly = monthly * 12;

  const destinations =
    rate.kind === 'miles'
      ? config.redemption_destinations.map((destination) => ({
          id: destination.id,
          label: destination.label,
          economyMonths: Math.ceil(destination.economy_miles / monthly),
          businessMonths: destination.business_miles
            ? Math.ceil(destination.business_miles / monthly)
            : undefined,
        }))
      : [];
  const [headline] = destinations;

  let line: string;
  if (rate.kind === 'cashback') {
    line = tr('cc_card_estimate_cashback_line', t, {
      yearly: formatCurrencyAmount(yearly, currency),
    });
  } else if (headline) {
    // The collapsed line names the first destination the market configured (FR-1.6.5).
    line = tr(
      'cc_card_estimate_miles_line',
      t,
      {
        destination: headline.label,
        months: String(headline.economyMonths),
      },
      headline.economyMonths,
    );
  } else {
    line = tr('cc_card_estimate_miles_yearly_line', t, {
      miles: count(yearly),
    });
  }

  const figures =
    rate.kind === 'cashback'
      ? [
          {
            label: t.cc_card_estimate_monthly_cashback,
            value: formatCurrencyAmount(monthly, currency),
          },
          {
            label: t.cc_card_estimate_yearly_cashback,
            value: formatCurrencyAmount(yearly, currency),
          },
        ]
      : [
          {
            label: t.cc_card_estimate_monthly_miles,
            value: tr('cc_card_estimate_miles_amount', t, {
              miles: count(monthly),
            }),
          },
          {
            label: t.cc_card_estimate_yearly_miles,
            value: tr('cc_card_estimate_miles_amount', t, {
              miles: count(yearly),
            }),
          },
        ];

  return (
    <section data-component="cc-product-card-estimate">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => {
          setExpanded(!expanded);
          onToggle?.(!expanded);
        }}
        className="flex min-h-11 w-full items-center gap-2.5 py-2.5 text-left"
      >
        <span
          aria-hidden="true"
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-fill/primary-light text-fill/primary-dark"
        >
          <MaterialIcon
            icon={rate.kind === 'miles' ? 'flight_takeoff' : 'paid'}
            px={16}
          />
        </span>
        <span className="flex min-w-0 grow flex-col gap-spacing/gap/soft-4px">
          <span
            className={classNames(
              CC_CARD_TEXT.note,
              'font-font-weight/medium text-text/secondary-lighter',
            )}
          >
            {tr('cc_card_estimate_prompt', t, { spend })}
          </span>
          <span
            className={classNames(CC_CARD_TEXT.value, 'text-fill/primary-dark')}
          >
            {line}
          </span>
        </span>
        <MaterialIcon
          icon={expanded ? 'expand_less' : 'expand_more'}
          px={16}
          className="shrink-0 text-text/secondary-lighter"
        />
      </button>
      <div
        id={panelId}
        hidden={!expanded}
        // `flex` would override the hidden attribute, so the display class follows the state too.
        className={classNames(
          'flex-col gap-spacing/gap/soft-8px rounded-radius/button p-spacing/padding/soft-12px bg-fill/alternative-1',
          expanded ? 'flex' : 'hidden',
        )}
      >
        <span
          className={classNames(
            CC_CARD_TEXT.label,
            'text-text/secondary-lighter',
          )}
        >
          {title}
        </span>
        <dl className="flex flex-col gap-spacing/gap/soft-6px">
          {figures.map((figure) => (
            <div
              key={figure.label}
              className="flex items-baseline justify-between gap-spacing/gap/soft-8px"
            >
              <dt
                className={classNames(
                  CC_CARD_TEXT.note,
                  'text-text/secondary-lighter',
                )}
              >
                {figure.label}
              </dt>
              <dd
                className={classNames(CC_CARD_TEXT.value, 'text-text/primary')}
              >
                {figure.value}
              </dd>
            </div>
          ))}
        </dl>
        {rate.kind === 'cashback' && minSpend && (
          <span
            className={classNames(
              CC_CARD_TEXT.note,
              'text-text/secondary-lighter',
            )}
          >
            {minSpend}
          </span>
        )}
        {destinations.length > 0 && (
          <ul className="mt-0.5 flex flex-col gap-spacing/gap/soft-4px border-t border-borders/default pt-2.5">
            {destinations.map((destination) => (
              <li
                key={destination.id}
                className={classNames(
                  CC_CARD_TEXT.note,
                  'flex flex-wrap items-baseline justify-between gap-x-spacing/gap/soft-8px',
                )}
              >
                <span className="font-font-weight/semibold text-text/primary">
                  {destination.label}
                </span>
                <span className="text-text/secondary-lighter">
                  {tr(
                    'cc_card_estimate_economy_months',
                    t,
                    { months: String(destination.economyMonths) },
                    destination.economyMonths,
                  )}
                  {destination.businessMonths !== undefined &&
                    ` · ${tr(
                      'cc_card_estimate_business_months',
                      t,
                      { months: String(destination.businessMonths) },
                      destination.businessMonths,
                    )}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        <span
          className={classNames(
            CC_CARD_TEXT.note,
            'text-text/secondary-lighter',
          )}
        >
          {tr('cc_card_estimate_assumption', t, {
            spend: formatCurrencyAmount(baseline, currency),
          })}
        </span>
      </div>
    </section>
  );
};
